import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

import { ListGroup, Form, Button, Modal } from "react-bootstrap";

import {
  PersonFill,
  Ban,
  ChatDots,
  ExclamationTriangle,
  Send,
} from "react-bootstrap-icons";

import { getUserState, getProfileState } from "../../app/selector";
import {
  loadProfile,
  profileUploaded,
} from "../../features/profile/profileSlice";

import { ReactComponent as Edit } from "../../resources/pencil.svg";
import { ReactComponent as Save } from "../../resources/save.svg";

import ConfirmationDialog from "../common/dialog/ConfirmationDialog";
import Notification from "../common/dialog/Notification";
import MessagePanel from "pages/common/popover/MessagePanel";

import {
  BACKEND_URL,
  PROFILE_INFO_FIELDS,
  PROFILE_LABELS,
  PROFILE_ICONS,
  HOROSCOPES,
  OCCUPATIONS,
  RELATIONSHIP_STATUSES,
  REPORT_REASONS,
  PET_OPTIONS,
  SIBLING_OPTIONS,
  KIDS_OPTIONS,
} from "constants";

const OPTIONS_BY_FIELD = {
  relationship_status: RELATIONSHIP_STATUSES,
  occupation: OCCUPATIONS,
  horoscope: HOROSCOPES,
  pet: PET_OPTIONS,
  sibling: SIBLING_OPTIONS,
  children_wish: KIDS_OPTIONS,
};

const ProfileIcon = ({ symbol }) => (
  <span className="profile-field-icon" role="img" aria-hidden="true">
    {symbol}
  </span>
);

const capitalize = (value = "") =>
  value ? value.charAt(0).toUpperCase() + value.slice(1) : "";

const getProfileInfo = (profile = {}) =>
  Object.keys(PROFILE_INFO_FIELDS).reduce((acc, field) => {
    acc[field] = profile[field] || "";
    return acc;
  }, {});

const getReasonLabel = (value) => {
  const found = REPORT_REASONS.find((reason) => reason.value === value);
  return found ? found.label : value;
};

const ScrollDropdownSelect = ({
  name,
  value,
  options,
  onChange,
  placeholder = "Select...",
  getOptionValue = (option) => option,
  getOptionLabel = (option) => option,
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (!showDropdown) return;

    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [showDropdown]);

  const selectedOption = options.find(
    (option) => getOptionValue(option) === value
  );

  const selectedLabel = selectedOption ? getOptionLabel(selectedOption) : "";

  const handleSelect = (selectedValue) => {
    onChange({
      target: {
        name,
        value: selectedValue,
      },
    });

    setShowDropdown(false);
  };

  return (
    <div className="profile-scroll-select" ref={dropdownRef}>
      <Button
        type="button"
        variant="outline-secondary"
        className="profile-scroll-select-button"
        onClick={() => setShowDropdown((prev) => !prev)}
      >
        <span>{selectedLabel || placeholder}</span>
        <span className="profile-scroll-select-arrow">▼</span>
      </Button>

      {showDropdown && (
        <div className="profile-scroll-select-menu">
          <div
            className={`profile-scroll-select-option ${!value ? "active" : ""}`}
            onClick={() => handleSelect("")}
            role="button"
            tabIndex={0}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                handleSelect("");
              }
            }}
          >
            {placeholder}
          </div>

          {options.map((option) => {
            const optionValue = getOptionValue(option);
            const optionLabel = getOptionLabel(option);

            return (
              <div
                key={optionValue}
                className={`profile-scroll-select-option ${value === optionValue ? "active" : ""
                  }`}
                onClick={() => handleSelect(optionValue)}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    handleSelect(optionValue);
                  }
                }}
              >
                {optionLabel}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const ProfileHeader = ({
  profile,
  isOwnProfile,
  isEditing,
  onMessageClick,
  onReportClick,
  onBlockClick,
  isReportCooldown,
}) => {
  const formattedUser = {
    username: capitalize(profile?.username),
    age: profile?.age || "",
    gender: profile?.gender ? profile.gender.charAt(0).toUpperCase() : "",
    state: capitalize(profile?.state),
  };

  const userText =
    `${formattedUser.username} ${formattedUser.age} ${formattedUser.gender} ${formattedUser.state}`.trim();

  return (
    <div className="profile-information-header">
      <h3 className="mb-2">
        <PersonFill className="me-2 text-primary" />
        {userText}
      </h3>

      {isOwnProfile ? (
        <Button
          variant="outline-success"
          className="round-button delete-button edit-button"
          type="submit"
        >
          {isEditing ? (
            <Save className="edit-icon" />
          ) : (
            <Edit className="edit-icon" />
          )}
        </Button>
      ) : (
        <div className="profile-action-buttons">
          <Button
            variant="outline-success"
            onClick={onMessageClick}
            className="round-button delete-button profile-action-button"
            title="Message"
            type="button"
          >
            <ChatDots />
          </Button>

          <Button
            variant="outline-warning"
            onClick={onReportClick}
            className="round-button delete-button profile-action-button"
            disabled={isReportCooldown}
            title="Report"
            type="button"
          >
            <ExclamationTriangle />
          </Button>

          <Button
            variant="outline-danger"
            onClick={onBlockClick}
            className="round-button delete-button profile-action-button"
            title="Block"
            type="button"
          >
            <Ban />
          </Button>
        </div>
      )}
    </div>
  );
};

const ProfileField = ({ field, value, isEditing, onChange }) => {
  const label = PROFILE_LABELS[field];
  const icon = PROFILE_ICONS[field];

  if (!label) return null;

  const renderInput = () => {
    if (!isEditing) {
      return (
        <Form.Control
          className="profile-field-control"
          type="text"
          name={field}
          value={value || ""}
          disabled
          readOnly
        />
      );
    }

    if (OPTIONS_BY_FIELD[field]) {
      return (
        <ScrollDropdownSelect
          name={field}
          value={value}
          options={OPTIONS_BY_FIELD[field]}
          onChange={onChange}
        />
      );
    }



    return (
      <Form.Control
        className="profile-field-control"
        type="text"
        name={field}
        value={value || ""}
        onChange={onChange}
      />
    );
  };

  return (
    <ListGroup.Item>
      <Form.Group className="profile-field-row" controlId={field}>
        <Form.Label className="profile-field-label" title={`${label}:`}>
          <ProfileIcon symbol={icon} />
          <span className="label-text">{label}:</span>
        </Form.Label>

        <div className="profile-field-value">{renderInput()}</div>
      </Form.Group>
    </ListGroup.Item>
  );
};

const ProfileInformation = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { view_user_id } = useParams();

  const isOwnProfile = !view_user_id;

  const { user } = useSelector(getUserState);
  const { profileInfoLoaded, profile: ownProfile } =
    useSelector(getProfileState);

  const [viewProfile, setViewProfile] = useState({});
  const [profileInfo, setProfileInfo] = useState(PROFILE_INFO_FIELDS);
  const [isEditing, setIsEditing] = useState(false);

  const [showMessageModal, setShowMessageModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);

  const [reportReason, setReportReason] = useState("");
  const [reportMessage, setReportMessage] = useState("");
  const [isReportCooldown, setIsReportCooldown] = useState(false);

  const [responseMessage, setResponseMessage] = useState({
    msg: "",
    className: "success",
  });
  const [showResponseMessage, setShowResponseMessage] = useState(false);

  const [showBlockConfirmation, setShowBlockConfirmation] = useState(false);
  const [showBlockNotification, setShowBlockNotification] = useState(false);
  const [blockSuccess, setBlockSuccess] = useState(true);

  const activeProfile = isOwnProfile ? user : viewProfile;

  const displayProfile = useMemo(() => {
    return isOwnProfile ? { ...user, ...ownProfile } : viewProfile;
  }, [isOwnProfile, user, ownProfile, viewProfile]);

  useEffect(() => {
    if (!isOwnProfile) return;

    if (!profileInfoLoaded && user?.user_id) {
      axios
        .post(`${BACKEND_URL}/get-profile-info`, {
          user_id: user.user_id,
        })
        .then((response) => {
          dispatch(
            loadProfile({
              profileInfo: response.data,
            })
          );
        })
        .catch((error) => {
          console.error(error);
        });
    }
  }, [isOwnProfile, profileInfoLoaded, user?.user_id, dispatch]);

  useEffect(() => {
    if (isOwnProfile || !view_user_id) return;

    axios
      .post(`${BACKEND_URL}/get-view-profile`, {
        view_user_id,
      })
      .then((response) => {
        setViewProfile(response.data);
      })
      .catch((error) => {
        if (error.response?.status === 403) {
          navigate("/no-permission");
        } else {
          console.error(error);
        }
      });
  }, [isOwnProfile, view_user_id, navigate]);

  useEffect(() => {
    if (isEditing) return;

    setProfileInfo(getProfileInfo(displayProfile));
  }, [displayProfile, isEditing]);

  useEffect(() => {
    if (!showResponseMessage) return;

    const timer = setTimeout(() => {
      setShowResponseMessage(false);
    }, 3000);

    return () => clearTimeout(timer);
  }, [showResponseMessage]);

  const handleChange = (event) => {
    const { name, value } = event.target;

    setProfileInfo((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleReportReasonChange = (event) => {
    setReportReason(event.target.value);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!isOwnProfile) return;

    if (!isEditing) {
      setIsEditing(true);
      return;
    }

    try {
      await axios.post(`${BACKEND_URL}/save-profile-info`, {
        user_id: user.user_id,
        ...profileInfo,
      });

      dispatch(
        loadProfile({
          profileInfo,
        })
      );

      dispatch(profileUploaded());

      setIsEditing(false);
    } catch (error) {
      console.error(error);
    }
  };

  const handleBlockUser = async () => {
    try {
      await axios.post(`${BACKEND_URL}/block-user`, {
        block_user_id: view_user_id,
      });

      setBlockSuccess(true);
      setShowBlockNotification(true);
    } catch (error) {
      setBlockSuccess(false);
      setShowBlockNotification(true);
      console.error(error);
    }
  };

  const handleReportClick = () => {
    if (isReportCooldown) {
      alert("Please wait before submitting another report.");
      return;
    }

    setShowReportModal(true);
  };

  const handleSendReport = async () => {
    const payload = {
      reason: getReasonLabel(reportReason),
      message: reportMessage,
      reported_user: view_user_id,
    };

    try {
      await axios.post(`${BACKEND_URL}/report-user`, payload, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      setResponseMessage({
        msg: "This user has been reported.",
        className: "success",
      });
      setShowResponseMessage(true);

      setReportReason("");
      setReportMessage("");
      setShowReportModal(false);

      setIsReportCooldown(true);
      setTimeout(() => setIsReportCooldown(false), 60000);
    } catch (error) {
      setResponseMessage({
        msg: "Something went wrong while reporting this user.",
        className: "danger",
      });
      setShowResponseMessage(true);

      console.error(error.response?.data || error.message);
    }
  };

  return (
    <div className="profile-information bg-light p-3 rounded">
      {!isOwnProfile && (
        <>
          <ConfirmationDialog
            text="Are you sure you want to block this user?"
            show={showBlockConfirmation}
            setShow={setShowBlockConfirmation}
            operation="Yes, block this user"
            variant="danger"
            onConfirm={handleBlockUser}
          />

          <Notification
            text={
              blockSuccess ? "User has been blocked!" : "Something went wrong!"
            }
            variant={blockSuccess ? "success" : "danger"}
            show={showBlockNotification}
            setShow={setShowBlockNotification}
            onClose={() => navigate("/")}
          />

          <Modal
            show={showMessageModal}
            onHide={() => setShowMessageModal(false)}
            centered
            className="profile-message-modal"
          >
            <Modal.Body>
              <div className="profile-message-modal-body">
                <MessagePanel
                  receiver_user_id={view_user_id}
                  setShow={setShowMessageModal}
                />
              </div>
            </Modal.Body>
          </Modal>

          <Modal
            show={showReportModal}
            onHide={() => setShowReportModal(false)}
            centered
            className="profile-report-modal"
          >
            <Modal.Header closeButton>
              <Modal.Title>Report User</Modal.Title>
            </Modal.Header>

            <Modal.Body>
              <Form>
                <Form.Group controlId="reportReason" className="modal-form-row">
                  <Form.Label>Select a reason</Form.Label>

                  <ScrollDropdownSelect
                    name="reportReason"
                    value={reportReason}
                    options={REPORT_REASONS}
                    onChange={handleReportReasonChange}
                    placeholder="-- Choose a reason --"
                    getOptionValue={(reason) => reason.value}
                    getOptionLabel={(reason) => reason.label}
                  />
                </Form.Group>

                <Form.Group
                  controlId="reportMessage"
                  className="modal-form-row mt-3"
                >
                  <Form.Label>Additional Details Optional</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    placeholder="Provide additional details..."
                    value={reportMessage}
                    onChange={(event) => setReportMessage(event.target.value)}
                  />
                </Form.Group>
              </Form>
            </Modal.Body>

            <Modal.Footer>
              <Button
                variant="outline-secondary"
                onClick={() => setShowReportModal(false)}
                type="button"
              >
                Cancel
              </Button>

              <Button
                variant="outline-success"
                onClick={handleSendReport}
                disabled={!reportReason}
                type="button"
              >
                <Send className="me-2" />
                Send
              </Button>
            </Modal.Footer>
          </Modal>
        </>
      )}

      <Form onSubmit={handleSubmit}>
        <ProfileHeader
          profile={activeProfile}
          isOwnProfile={isOwnProfile}
          isEditing={isEditing}
          onMessageClick={() => setShowMessageModal(true)}
          onReportClick={handleReportClick}
          onBlockClick={() => setShowBlockConfirmation(true)}
          isReportCooldown={isReportCooldown}
        />

        <ListGroup>
          {Object.entries(profileInfo).map(([field, value]) => (
            <ProfileField
              key={field}
              field={field}
              value={value}
              isEditing={isOwnProfile && isEditing}
              onChange={handleChange}
            />
          ))}
        </ListGroup>

        {!isOwnProfile && (
          <div
            className={`alert alert-${responseMessage.className}`}
            role="alert"
            style={{
              opacity: showResponseMessage ? 1 : 0,
              transition: "opacity 1s ease-out",
            }}
          >
            {responseMessage.msg}
          </div>
        )}
      </Form>
    </div>
  );
};

export default ProfileInformation;